import axios, { AxiosError, CanceledError, type AxiosRequestConfig } from 'axios';
import { toast } from '@/ui/toast';
import { observeRuntimeReleaseHeaders } from '@/client/runtime-release-store';
import { extractApiErrorMessage } from '@/shared/errors/api-error';

declare module 'axios' {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
    interface AxiosRequestConfig<D = any> {
        errorToastMode?: 'default' | 'suppress';
    }
}

const prepareErrorToShow = (data: string | object) => {
    let result: string = '';

    if (typeof data === 'string') {
        return result + data;
    }

    if (typeof data === 'object') {
        for (const key in data) {
            result += `{ [${key}]: ${prepareErrorToShow((data as Record<string, object | string>)[key])} }`;
        }
    }

    return result;
};

export const getAxiosInstance = (baseURL: string) => {
    const _axios = axios.create({ baseURL });

    _axios.interceptors.request.use(
        (value) => value,
        (error) => {
            if (error instanceof CanceledError) {
                return Promise.reject(error);
            }

            if (error instanceof AxiosError && error.config?.errorToastMode === 'suppress') {
                return Promise.reject(error);
            }

            if (error instanceof AxiosError) {
                toast.error(error.message);
            } else {
                toast.error('Произошла ошибка');
            }
            return Promise.reject(error);
        },
    );

    _axios.interceptors.response.use(
        (value) => {
            observeRuntimeReleaseHeaders(value.headers);
            return value;
        },
        (error) => {
            if (error instanceof CanceledError) {
                return Promise.reject(error);
            }

            if (error instanceof AxiosError && error.config?.errorToastMode === 'suppress') {
                return Promise.reject(error);
            }

            if (error instanceof AxiosError) {
                observeRuntimeReleaseHeaders(error.response?.headers);
                const responseMessage = extractApiErrorMessage(error.response?.data);
                const message =
                    responseMessage ?? (error.response?.data ? prepareErrorToShow(error.response.data) : error.message);
                const status = error.response?.status ?? error.status ?? 'network';
                toast.error(`Ошибка ${status}: ${message}`);
            } else {
                toast.error('Произошла ошибка');
            }
            return Promise.reject(error);
        },
    );

    return _axios;
};

export const withSuppressedErrorToast = <TConfig extends AxiosRequestConfig>(config: TConfig): TConfig => ({
    ...config,
    errorToastMode: 'suppress',
});
